import { Fragment, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { api } from "@/api/client";
import {
  queryKeys,
  useReachability,
  useSimulation,
  useSimulationCompare,
  useSimulationReport,
  useSimulations,
} from "@/api/queries";
import type { Simulation, SimulationCompare, SimulationPath } from "@/api/types";
import { Button } from "@/components/Button";
import { DefinitionList } from "@/components/DefinitionList";
import { Panel, SectionTitle } from "@/components/Panel";
import { Meter } from "@/components/Meter";
import { Modal } from "@/components/Modal";
import { RemoteState } from "@/components/RemoteState";
import { StatusLabel } from "@/components/StatusLabel";
import { Tag } from "@/components/Tag";
import { formatNumber, formatPercent } from "@/lib/cn";
import { useUiStore } from "@/store/ui";

const PLAYER_LABEL: Record<string, string> = {
  SAFE_SURVIVAL: "求稳生存",
  RELATION_FIRST: "关系优先",
  POWER_FIRST: "事业/权力优先",
  RISK_EXPLORER: "高风险探索",
  MORAL_ABSOLUTE: "道德绝对主义",
  MIN_INFO: "信息最少路径",
  SAVE_SCUMMER: "读档最优解（实验性）",
};

/**
 * 路径模拟（specs/07 §4 · §10.3 / PR-141）。
 *
 * 六类玩家各自跑一遍真实状态机；死路、结局覆盖与素材覆盖率都由此得出。
 * "结局可达性"走独立接口，用穷举组合证明每张配置结局都存在可达路径。
 *
 * @dataSource remote
 */
export function SimulateView() {
  const projectId = useUiStore((state) => state.projectId);
  const pushToast = useUiStore((state) => state.pushToast);
  const queryClient = useQueryClient();

  const simulation = useSimulation(projectId);
  const reachability = useReachability(projectId);
  const history = useSimulations(projectId);
  const simulationId = simulation.data?.id ?? null;

  /**
   * 正在回看的**快照 ID**。
   *
   * F-5 之前后端没有列表路由（该路径只挂 `POST`，`GET` 返 405），所以回看只能是
   * "本次模拟恰好有 ID 就看一眼"；现在列表在这里，`reportId` 由用户从历史里选。
   */
  const [reportId, setReportId] = useState<string | null>(null);
  const report = useSimulationReport(reportId);

  /* ------------------------------------------------- 差异对比（F-7） */
  /** 选中用于比较的历史记录 ID（最多选 2 条）。 */
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const compareIds =
    selectedIds.length === 2 ? { baseId: selectedIds[0], targetId: selectedIds[1] } : null;
  const compare = useSimulationCompare(compareIds?.baseId ?? null, compareIds?.targetId ?? null);
  const [showCompare, setShowCompare] = useState(false);

  /** 正在回放的**单条路径**（需求 §12.4：单路径回放 + 状态时间线）。 */
  const [replayPath, setReplayPath] = useState<SimulationPath | null>(null);

  const rerun = useMutation({
    mutationFn: () => api.post<Simulation>(`/projects/${projectId}/simulations`, { persist: true }),
    onSuccess: (result) => {
      queryClient.setQueryData(queryKeys.simulations(projectId), result);
      // 刚落库的这一次要出现在历史里，否则"跑完看不到自己"（缓存与真机不一致）
      void queryClient.invalidateQueries({ queryKey: queryKeys.simulationHistory(projectId) });
      pushToast({
        title: result.issues.length ? "模拟完成，存在告警" : "模拟完成",
        detail: result.issues[0]?.message ?? "六类玩家均未触发死路",
        mark: result.issues.length ? "!" : "✓",
      });
    },
  });

  const data = simulation.data;
  const coverage = data?.coverage;
  const endingCoverage = coverage?.ending_coverage ?? {};
  const endingIds = Object.keys(endingCoverage);
  const reachable = endingIds.filter((id) => (endingCoverage[id] ?? 0) > 0).length;
  const deadEnds = (data?.paths ?? []).filter((path) => path.failures.length > 0).length;

  /* §19.2「≥ 2 种锁点组合/结局」：口径由后端 `enumerate_paths` 给出（specs/06 §8.2）。 */
  const lockCombinationCounts = reachability.data?.ending_lock_combination_counts ?? {};
  const lockedEndings = Object.keys(lockCombinationCounts).length;
  const endingsWithMultipleCombos = reachability.data?.endings_with_multiple_lock_combinations ?? 0;

  /** 切换选中状态（最多选 2 条）。 */
  function toggleSelected(id: string) {
    setSelectedIds((prev) => {
      if (prev.includes(id)) return prev.filter((x) => x !== id);
      if (prev.length >= 2) return [prev[1], id];
      return [...prev, id];
    });
  }

  return (
    <div className="page-scroll">
      <div className="page-width">
        <RemoteState queries={[simulation, reachability, history]} title="路径模拟加载失败">
          <header className="page-heading">
            <div>
              <span className="eyebrow">六类玩家 · 路径与结局覆盖</span>
              <h2>先让六种人各走一遍，再谈这个结构成不成立</h2>
              <p>
                模拟器使用与校验相同的状态机：每个选择只调用已声明的
                effects，任何"靠作者脑补推进"的段落都会在这里暴露为死路。
              </p>
            </div>
            <div className="toolbar-right">
              <Button
                variant="quiet"
                id="simulation-report"
                disabled={!simulationId}
                onClick={() => setReportId(simulationId)}
              >
                打开本次报告
              </Button>
              <Button variant="primary" disabled={rerun.isPending} onClick={() => rerun.mutate()}>
                {rerun.isPending ? "模拟中…" : "重新模拟"}
              </Button>
            </div>
          </header>

          <section className="check-hero">
            <div className="check-score">
              <div className="check-score-number">
                {reachable}/{endingIds.length || 6}
                <small>结局可达 / 配置结局</small>
              </div>
              <div>
                <h2>{deadEnds > 0 ? `${deadEnds} 类玩家走进死路` : "六类玩家均未触发死路"}</h2>
                <p>
                  共枚举 {reachability.data?.combinations ?? 0} 种选择组合
                  {reachability.data?.lock_combinations_total != null
                    ? `（其中 ${reachability.data.lock_combinations_total} 种锁点组合）`
                    : ""}
                  ；结局优先级（ending_priority）决定同时满足多张配置卡时的匹配顺序。
                </p>
              </div>
            </div>
            <div className="check-stat-grid">
              <div className="check-stat">
                <strong>{formatPercent(coverage?.storylet_coverage ?? 0, 1)}</strong>
                <span>Storylet 覆盖</span>
              </div>
              <div className="check-stat">
                <strong>{formatPercent(coverage?.choice_coverage ?? 0, 1)}</strong>
                <span>选择覆盖</span>
              </div>
              <div className="check-stat">
                <strong>{(data?.paths ?? []).length}</strong>
                <span>模拟路径</span>
              </div>
              <div className="check-stat">
                <strong>{reachability.data?.combinations ?? 0}</strong>
                <span>枚举组合</span>
              </div>
            </div>
          </section>

          <SectionTitle
            title="六类玩家路径"
            description="每类玩家的偏好不同，但都必须抵达一个结局"
          />
          <div className="player-grid">
            {(data?.paths ?? []).map((path) => (
              <PlayerCard key={path.player_type} path={path} onReplay={setReplayPath} />
            ))}
          </div>

          <PlayerAssetSummary paths={data?.paths ?? []} />

          <SectionTitle
            title="结局可达性"
            description="穷举锁点组合，证明每张配置结局都存在可达路径"
          />
          <Panel
            title="配置结局覆盖"
            description={
              lockedEndings > 0
                ? `计数为该结局在枚举结果中出现的路径数；≥ 2 种锁点组合的结局 ${endingsWithMultipleCombos}/${lockedEndings}`
                : "计数为该结局在枚举结果中出现的路径数"
            }
            bodyClassName="progress-list"
          >
            {endingIds.length === 0 ? (
              <p className="inline-note">暂无结局覆盖数据，先运行一次模拟。</p>
            ) : (
              endingIds.map((endingId) => {
                const count = endingCoverage[endingId] ?? 0;
                return (
                  <div className="progress-row" key={endingId}>
                    <span>
                      <strong>{endingId}</strong>
                      <small
                        style={{
                          display: "block",
                          color: "var(--ink-faint)",
                          fontSize: "9px",
                          marginTop: "3px",
                        }}
                      >
                        {count === 0 ? "不可达 · 需修复" : count < 3 ? "可达但偏窄" : "可达"}
                        {lockCombinationCounts[endingId] != null
                          ? ` · ${lockCombinationCounts[endingId]} 种锁点组合`
                          : ""}
                      </small>
                    </span>
                    <div className="progress-track">
                      <span
                        style={{
                          width: `${Math.min(100, count * 20)}%`,
                          background:
                            count === 0 ? "var(--red)" : count < 3 ? "var(--gold)" : "var(--green)",
                        }}
                      />
                    </div>
                    <strong>{count}</strong>
                  </div>
                );
              })
            )}
          </Panel>

          <SectionTitle
            title="历史模拟"
            description="每次模拟都落库成快照；产物之后怎么改，都不会改变当时那条结论"
          />
          <Panel
            title={
              <span style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <span>最近记录</span>
                {selectedIds.length === 2 ? (
                  <Button
                    variant="primary"
                    size="small"
                    onClick={() => {
                      setShowCompare(true);
                    }}
                  >
                    比较选中的两次
                  </Button>
                ) : (
                  <span style={{ fontSize: "11px", color: "var(--ink-faint)", fontWeight: 400 }}>
                    {selectedIds.length === 1 ? "请再选一条" : "勾选两条记录以比较差异"}
                  </span>
                )}
              </span>
            }
            description="回看读的是落库那一刻的快照，不是当前结构重算的结果"
          >
            {(history.data ?? []).length === 0 ? (
              <p className="inline-note">还没有历史记录，先运行一次模拟。</p>
            ) : (
              <ul className="activity-list" style={{ margin: 0, padding: 0 }}>
                {(history.data ?? []).map((item) => (
                  <li
                    className="activity-item"
                    key={item.id}
                    style={{
                      background: selectedIds.includes(item.id) ? "var(--violet-soft)" : undefined,
                      borderRadius: 6,
                    }}
                  >
                    <label
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        cursor: "pointer",
                        flex: 1,
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={selectedIds.includes(item.id)}
                        onChange={() => toggleSelected(item.id)}
                        style={{ accentColor: "var(--accent)" }}
                      />
                      <span className="mini-mark">{item.issues_count > 0 ? "!" : "◇"}</span>
                      <div>
                        <p>
                          {item.paths_count} 条玩家路径 ·{" "}
                          {item.issues_count > 0 ? `${item.issues_count} 条告警` : "无告警"}
                        </p>
                        <small>
                          {formatMoment(item.created_at)} · {item.id}
                        </small>
                      </div>
                    </label>
                    <Button variant="quiet" onClick={() => setReportId(item.id)}>
                      回看
                    </Button>
                  </li>
                ))}
              </ul>
            )}
          </Panel>

          {(data?.issues ?? []).length > 0 ? (
            <>
              <SectionTitle title="模拟告警" description="不阻塞交付，但需要在送审前给出结论" />
              <ul className="issue-list">
                {(data?.issues ?? []).map((issue, index) => (
                  <li className="issue-item" key={index}>
                    <span className="mini-mark">{issue.severity === "error" ? "!" : "?"}</span>
                    <div>
                      <p>
                        {issue.rule_id} · {issue.message}
                      </p>
                      <small>{issue.suggestion}</small>
                    </div>
                    <StatusLabel tone={issue.severity === "error" ? "danger" : "warning"}>
                      {issue.severity === "error" ? "阻塞" : "告警"}
                    </StatusLabel>
                  </li>
                ))}
              </ul>
            </>
          ) : null}
        </RemoteState>
      </div>

      {/* ---- 历史报告回看 Modal ---- */}
      <Modal
        open={reportId !== null}
        onClose={() => setReportId(null)}
        className="modal-wide"
        eyebrow="历史报告回看"
        title="已持久化的模拟快照"
        footer={
          <Button variant="quiet" onClick={() => setReportId(null)}>
            关闭
          </Button>
        }
      >
        <RemoteState queries={[report]} title="报告加载失败">
          {report.data ? <ReportBody report={report.data} /> : null}
        </RemoteState>
      </Modal>

      {/* ---- 差异对比 Modal ---- */}
      <Modal
        open={showCompare}
        onClose={() => setShowCompare(false)}
        className="modal-wide"
        eyebrow="模拟历史比较"
        title="两次模拟结果差异"
        footer={
          <Button variant="quiet" onClick={() => setShowCompare(false)}>
            关闭
          </Button>
        }
      >
        <RemoteState queries={[compare]} title="比较加载失败">
          {compare.data ? <CompareBody diff={compare.data} /> : null}
        </RemoteState>
      </Modal>

      {/* ---- 单路径回放 Modal（§12.4） ---- */}
      <Modal
        open={replayPath !== null}
        onClose={() => setReplayPath(null)}
        className="modal-wide"
        eyebrow="单路径回放"
        title="逐步状态与增量"
        footer={
          <Button variant="quiet" onClick={() => setReplayPath(null)}>
            关闭
          </Button>
        }
      >
        {replayPath ? <PathReplay path={replayPath} /> : null}
      </Modal>
    </div>
  );
}

/**
 * 报告快照的只读呈现。
 *
 * 与页面主体的区别：主体是「当前结构跑一遍」的结果，这里读的是**落库那一刻**的快照，
 * 因此产物被改动之后依然能回看当时的结论。
 */
function ReportBody({ report }: { report: Simulation }) {
  const endingCoverage = report.coverage.ending_coverage ?? {};
  const endingIds = Object.keys(endingCoverage);
  const reachable = endingIds.filter((id) => (endingCoverage[id] ?? 0) > 0).length;
  const deadEnds = report.paths.filter((path) => path.failures.length > 0).length;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div className="meta-row">
        <StatusLabel tone={report.status === "SUCCEEDED" ? "success" : "warning"}>
          {report.status}
        </StatusLabel>
        <Tag>{report.id}</Tag>
        <Tag tone={deadEnds > 0 ? "red" : "green"}>
          {deadEnds > 0 ? `${deadEnds} 类玩家死路` : "无死路"}
        </Tag>
      </div>

      <DefinitionList
        entries={[
          { term: "结局可达", value: `${reachable} / ${endingIds.length}` },
          {
            term: "Storylet 覆盖",
            value: formatPercent(report.coverage.storylet_coverage ?? 0, 1),
          },
          { term: "选择覆盖", value: formatPercent(report.coverage.choice_coverage ?? 0, 1) },
          { term: "模拟路径", value: formatNumber(report.paths.length) },
          { term: "玩家类型", value: report.player_types.join(" · ") || "—" },
          { term: "方法包", value: report.methodology_version ?? "—" },
          { term: "Profile", value: report.profile_version ?? "—" },
        ]}
      />

      <section>
        <h3>各玩家路径</h3>
        <ul className="activity-list">
          {report.paths.map((path) => (
            <li className="activity-item" key={path.player_type}>
              <span className="mini-mark">{path.failures.length > 0 ? "!" : "◇"}</span>
              <div>
                <p>
                  {PLAYER_LABEL[path.player_type] ?? path.player_type} →{" "}
                  {path.ending_id ?? "无结局"}
                </p>
                <small>
                  {path.steps.length} 步 · 未偿债务 {path.unpaid_debts.length} · 未登场{" "}
                  {path.absent_characters.length}
                </small>
              </div>
              <StatusLabel tone={path.failures.length > 0 ? "danger" : "success"}>
                {path.failures.length > 0 ? "死路" : "抵达"}
              </StatusLabel>
            </li>
          ))}
        </ul>
      </section>

      {report.issues.length > 0 ? (
        <section>
          <h3>快照告警</h3>
          <ul className="issue-list">
            {report.issues.map((issue, index) => (
              <li className="issue-item" key={index}>
                <span className="mini-mark">{issue.severity === "error" ? "!" : "?"}</span>
                <div>
                  <p>
                    {issue.rule_id} · {issue.message}
                  </p>
                  <small>{issue.suggestion}</small>
                </div>
                <StatusLabel tone={issue.severity === "error" ? "danger" : "warning"}>
                  {issue.severity === "error" ? "阻塞" : "告警"}
                </StatusLabel>
              </li>
            ))}
          </ul>
        </section>
      ) : null}
    </div>
  );
}

/**
 * 差异对比面板（F-7）。
 *
 * 展示三个区：
 * 1. Coverage 对比表
 * 2. 路径对比（每类玩家一行）
 * 3. 问题对比（unchanged / resolved / introduced）
 */
function CompareBody({ diff }: { diff: SimulationCompare }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      {/* 元信息 */}
      <div className="meta-row">
        <Tag>基线: {diff.base_id}</Tag>
        <Tag>目标: {diff.target_id}</Tag>
        {diff.methodology_changed ? <Tag tone="gold">方法包变更</Tag> : null}
        {diff.profile_changed ? <Tag tone="gold">Profile 变更</Tag> : null}
      </div>

      {/* 1. Coverage 对比 */}
      <section>
        <h3>覆盖率对比</h3>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
          <thead>
            <tr style={{ borderBottom: "1px solid var(--surface-border, var(--line))" }}>
              <th style={{ textAlign: "left", padding: "6px 8px" }}>指标</th>
              <th style={{ textAlign: "right", padding: "6px 8px" }}>基线</th>
              <th style={{ textAlign: "right", padding: "6px 8px" }}>目标</th>
              <th style={{ textAlign: "right", padding: "6px 8px" }}>变化</th>
            </tr>
          </thead>
          <tbody>
            {Object.entries(diff.coverage_diff).map(([key, item]) => (
              <tr
                key={key}
                style={{ borderBottom: "1px solid var(--surface-border, var(--line))" }}
              >
                <td style={{ padding: "5px 8px" }}>{key}</td>
                <td
                  style={{
                    padding: "5px 8px",
                    textAlign: "right",
                    fontVariantNumeric: "tabular-nums",
                  }}
                >
                  {formatDiffValue(item.base)}
                </td>
                <td
                  style={{
                    padding: "5px 8px",
                    textAlign: "right",
                    fontVariantNumeric: "tabular-nums",
                  }}
                >
                  {formatDiffValue(item.target)}
                </td>
                <td
                  style={{
                    padding: "5px 8px",
                    textAlign: "right",
                    fontVariantNumeric: "tabular-nums",
                    color: diffColor(item.change),
                    fontWeight: item.change !== 0 ? 600 : 400,
                  }}
                >
                  {item.change > 0 ? "+" : ""}
                  {formatDiffValue(item.change)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      {/* 2. 路径对比 */}
      <section>
        <h3>路径对比</h3>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
          <thead>
            <tr style={{ borderBottom: "1px solid var(--surface-border, var(--line))" }}>
              <th style={{ textAlign: "left", padding: "6px 8px" }}>玩家类型</th>
              <th style={{ textAlign: "center", padding: "6px 8px" }}>结局一致</th>
              <th style={{ textAlign: "right", padding: "6px 8px" }}>基线步数</th>
              <th style={{ textAlign: "right", padding: "6px 8px" }}>目标步数</th>
              <th style={{ textAlign: "right", padding: "6px 8px" }}>已解决失败</th>
              <th style={{ textAlign: "right", padding: "6px 8px" }}>新增失败</th>
            </tr>
          </thead>
          <tbody>
            {diff.path_diffs.map((pd) => (
              <tr
                key={pd.player_type}
                style={{ borderBottom: "1px solid var(--surface-border, var(--line))" }}
              >
                <td style={{ padding: "5px 8px" }}>
                  {PLAYER_LABEL[pd.player_type] ?? pd.player_type}
                  <br />
                  <small style={{ color: "var(--ink-faint)" }}>
                    {pd.base_ending ?? "无"} → {pd.target_ending ?? "无"}
                  </small>
                </td>
                <td style={{ padding: "5px 8px", textAlign: "center" }}>
                  {pd.same_ending ? (
                    <span style={{ color: "var(--green)" }}>✓</span>
                  ) : (
                    <span style={{ color: "var(--red)" }}>✗</span>
                  )}
                </td>
                <td style={{ padding: "5px 8px", textAlign: "right" }}>{pd.base_steps}</td>
                <td style={{ padding: "5px 8px", textAlign: "right" }}>{pd.target_steps}</td>
                <td
                  style={{
                    padding: "5px 8px",
                    textAlign: "right",
                    color: pd.failures_resolved > 0 ? "var(--green)" : "inherit",
                  }}
                >
                  {pd.failures_resolved}
                </td>
                <td
                  style={{
                    padding: "5px 8px",
                    textAlign: "right",
                    color: pd.failures_introduced > 0 ? "var(--red)" : "inherit",
                  }}
                >
                  {pd.failures_introduced}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      {/* 3. 问题对比 */}
      <section>
        <h3>问题对比</h3>
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <IssueGroup
            title="未变化"
            color="var(--ink-muted)"
            items={diff.issues_diff.unchanged}
            emptyText="无共同问题"
          />
          <IssueGroup
            title="已解决"
            color="var(--green)"
            items={diff.issues_diff.resolved}
            emptyText="无已解决问题"
          />
          <IssueGroup
            title="新增"
            color="var(--red)"
            items={diff.issues_diff.introduced}
            emptyText="无新增问题"
          />
        </div>
      </section>
    </div>
  );
}

/**
 * 单组问题渲染。
 *
 * 每个问题显示 rule_id 和 message，颜色表示类型。
 */
function IssueGroup({
  title,
  color,
  items,
  emptyText,
}: {
  title: string;
  color: string;
  items: Record<string, unknown>[];
  emptyText: string;
}) {
  if (items.length === 0) {
    return (
      <div
        style={{
          borderLeft: `3px solid ${color}`,
          paddingLeft: 10,
          opacity: 0.6,
        }}
      >
        <strong style={{ fontSize: "12px", color }}>{title}</strong>
        <p className="inline-note" style={{ marginTop: 2 }}>
          {emptyText}
        </p>
      </div>
    );
  }
  return (
    <div style={{ borderLeft: `3px solid ${color}`, paddingLeft: 10 }}>
      <strong style={{ fontSize: "12px", color }}>
        {title}（{items.length}）
      </strong>
      <ul className="issue-list" style={{ marginTop: 6 }}>
        {items.map((item, index) => (
          <li className="issue-item" key={index}>
            <span className="mini-mark">
              {String(item.severity ?? "?") === "error" ? "!" : "?"}
            </span>
            <div>
              <p>
                {String(item.rule_id ?? "?")} · {String(item.message ?? "")}
              </p>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * 时间展示到分钟。
 *
 * 后端给的是带时区的 ISO 串；这里按**本地时区**渲染，所以同一份快照在东八区与
 * UTC 下会显示不同的小时数——这是刻意的（用户看的是自己的时间）。
 * 因此断言历史列表时不要断言时间文本，断言 ID / 条数 / 告警数这类稳定字段。
 */
function formatMoment(value: string | null): string {
  if (!value) return "时间未知";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  const pad = (part: number) => String(part).padStart(2, "0");
  return (
    `${parsed.getFullYear()}-${pad(parsed.getMonth() + 1)}-${pad(parsed.getDate())}` +
    ` ${pad(parsed.getHours())}:${pad(parsed.getMinutes())}`
  );
}

function PlayerCard({
  path,
  onReplay,
}: {
  path: SimulationPath;
  onReplay?: (path: SimulationPath) => void;
}) {
  const deadEnd = path.failures.length > 0;
  const stepCount = path.steps.length;
  const lastStep = path.steps[stepCount - 1];

  return (
    <article className="player-card">
      <header>
        <h4>{PLAYER_LABEL[path.player_type] ?? path.player_type}</h4>
        {deadEnd ? (
          <StatusLabel tone="danger">死路</StatusLabel>
        ) : (
          <StatusLabel tone="success">抵达</StatusLabel>
        )}
      </header>
      <p className="inline-note">{path.ending_explanation || "—"}</p>
      <div className="meta-row" style={{ marginTop: 10 }}>
        <Tag tone={deadEnd ? "red" : "green"}>{path.ending_id ?? "无结局"}</Tag>
        <Tag>{stepCount} 步</Tag>
      </div>
      <Meter value={Math.min(100, stepCount * 8)} label={`${path.player_type} 路径长度`} />
      <dl>
        <dt>最后节点</dt>
        <dd>{String((lastStep as Record<string, unknown> | undefined)?.storylet_id ?? "—")}</dd>
        <dt>未偿债务</dt>
        <dd>{path.unpaid_debts.length}</dd>
        <dt>未登场人物</dt>
        <dd>{path.absent_characters.length}</dd>
        <dt>失败项</dt>
        <dd>{path.failures.length}</dd>
      </dl>
      {renderAssetProduction(path)}
      {renderChoices(path)}
      {deadEnd ? (
        <p className="inline-note" style={{ marginTop: 8 }}>
          {String(
            (path.failures[0] as Record<string, unknown> | undefined)?.message ?? "该路径无法继续",
          )}
        </p>
      ) : null}
      {onReplay ? (
        <div className="toolbar-right" style={{ marginTop: 10 }}>
          <Button size="small" variant="quiet" onClick={() => onReplay(path)}>
            路径回放
          </Button>
        </div>
      ) : null}
    </article>
  );
}

/* ------------------------------------------------------------- 格式化辅助 */

/**
 * 格式化 diff 表格中的数值。
 * 百分比值保留两位小数，整数直接显示。
 */
function formatDiffValue(value: number): string {
  if (Number.isInteger(value)) return String(value);
  // 百分比值通常小于 1 或大于 1 的小数，保留 2 位
  if (Math.abs(value) < 10) return value.toFixed(2);
  return value.toFixed(1);
}

/**
 * 变化值的颜色：正 = 绿，负 = 红，零 = 默认。
 */
function diffColor(value: number): string | undefined {
  if (value > 0) return "var(--green)";
  if (value < 0) return "var(--red)";
  return undefined;
}

/* ------------------------------------------------------------- 四字段（PR-141/151/152） */

const ASSET_LABELS: Record<string, string> = {
  new_scenes: "新场景",
  new_characters: "新角色",
  new_wardrobe: "新服装",
  new_props: "新道具",
  new_shots: "新镜头",
};

/**
 * 单条路径的素材增量 + 制作等级（PR-151 / PR-152）。
 *
 * 放在 PlayerCard 内联；`budget_warnings` 不在此消费（去重决策：预算告警
 * 已由 `simulate()` 提升为 issues 里的 R13，由「快照告警」区统一呈现）。
 * 字段为空的（离线 mock 的简化 / 老快照未补齐）自动隐藏，不报错。
 */
function renderAssetProduction(path: SimulationPath) {
  const assets = path.asset_increments ?? {};
  const prod = path.production_class_summary ?? {};
  const assetKeys = Object.keys(assets);
  const prodKeys = Object.keys(prod);
  if (assetKeys.length === 0 && prodKeys.length === 0) return null;
  return (
    <div style={{ marginTop: 8, display: "flex", flexDirection: "column", gap: 4 }}>
      {assetKeys.length > 0 ? (
        <dl
          style={{
            display: "grid",
            gridTemplateColumns: "auto 1fr",
            gap: "2px 10px",
            margin: 0,
            fontSize: "11px",
          }}
        >
          {assetKeys.map((key) => (
            <Fragment key={key}>
              <dt style={{ color: "var(--ink-faint)" }}>{ASSET_LABELS[key] ?? key}</dt>
              <dd style={{ margin: 0, textAlign: "right" }}>{assets[key] ?? 0}</dd>
            </Fragment>
          ))}
        </dl>
      ) : null}
      {prodKeys.length > 0 ? (
        <dl
          style={{
            display: "grid",
            gridTemplateColumns: "auto 1fr",
            gap: "2px 10px",
            margin: 0,
            fontSize: "11px",
          }}
        >
          {prodKeys.map((key) => (
            <Fragment key={key}>
              <dt style={{ color: "var(--ink-faint)" }}>{key} 级</dt>
              <dd style={{ margin: 0, textAlign: "right" }}>{prod[key] ?? 0}</dd>
            </Fragment>
          ))}
        </dl>
      ) : null}
    </div>
  );
}

/**
 * 单条路径的决策原因（PR-141）。
 *
 * `choices_made` 是该玩家在每个选择节点选某选项的原因；为空时隐藏。
 */
function renderChoices(path: SimulationPath) {
  const choices = path.choices_made ?? [];
  if (choices.length === 0) return null;
  return (
    <ul className="activity-list" style={{ marginTop: 8, padding: 0 }}>
      {choices.map((reason, index) => (
        <li className="activity-item" key={index} style={{ padding: "2px 0" }}>
          <span className="mini-mark">◇</span>
          <span style={{ fontSize: "11px", color: "var(--ink-soft)" }}>{reason}</span>
        </li>
      ))}
    </ul>
  );
}

/**
 * 跨玩家汇总的素材增量 × 制作等级面板（PR-151 / PR-152）。
 *
 * 按玩家为列聚合全部路径的 `asset_increments` 与 `production_class_summary`，
 * 末列给出跨玩家合计。`budget_warnings` 不在此消费（去重决策，同 `renderAssetProduction`）。
 * 所有路径都缺字段（离线 mock / 老快照）时不渲染，避免空面板。
 */
function PlayerAssetSummary({ paths }: { paths: SimulationPath[] }) {
  const assetKeys = [...new Set(paths.map((p) => Object.keys(p.asset_increments ?? {})).flat())];
  const prodKeys = [
    ...new Set(paths.map((p) => Object.keys(p.production_class_summary ?? {})).flat()),
  ];
  if (assetKeys.length === 0 && prodKeys.length === 0) return null;

  const sumBy = (
    rows: SimulationPath[],
    keys: string[],
    pick: (p: SimulationPath) => Record<string, number>,
  ) => keys.map((key) => rows.reduce((sum, p) => sum + ((pick(p) ?? {})[key] ?? 0), 0));

  const assetSums = sumBy(paths, assetKeys, (p) => p.asset_increments ?? {});
  const prodSums = sumBy(paths, prodKeys, (p) => p.production_class_summary ?? {});

  return (
    <Panel
      title="素材增量 × 制作等级"
      description="各玩家在其路径上新增的素材量与选项制作等级分布（跨玩家合计见末列）"
      bodyClassName="activity-list"
    >
      {assetKeys.length > 0 ? (
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
          <thead>
            <tr style={{ borderBottom: "1px solid var(--line)" }}>
              <th style={{ textAlign: "left", padding: "6px 8px" }}>素材增量</th>
              {paths.map((p) => (
                <th
                  key={p.player_type}
                  style={{
                    textAlign: "right",
                    padding: "6px 8px",
                    fontSize: "11px",
                    fontWeight: 500,
                  }}
                >
                  {PLAYER_LABEL[p.player_type] ?? p.player_type}
                </th>
              ))}
              <th style={{ textAlign: "right", padding: "6px 8px" }}>合计</th>
            </tr>
          </thead>
          <tbody>
            {assetKeys.map((key, i) => (
              <tr key={key} style={{ borderBottom: "1px solid var(--line-soft, var(--line))" }}>
                <td style={{ padding: "5px 8px", color: "var(--ink-faint)" }}>
                  {ASSET_LABELS[key] ?? key}
                </td>
                {paths.map((p) => (
                  <td
                    key={p.player_type}
                    style={{
                      padding: "5px 8px",
                      textAlign: "right",
                      fontVariantNumeric: "tabular-nums",
                    }}
                  >
                    {(p.asset_increments ?? {})[key] ?? 0}
                  </td>
                ))}
                <td
                  style={{
                    padding: "5px 8px",
                    textAlign: "right",
                    fontWeight: 500,
                    fontVariantNumeric: "tabular-nums",
                  }}
                >
                  {assetSums[i]}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : null}
      {prodKeys.length > 0 ? (
        <table
          style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px", marginTop: 12 }}
        >
          <thead>
            <tr style={{ borderBottom: "1px solid var(--line)" }}>
              <th style={{ textAlign: "left", padding: "6px 8px" }}>制作等级</th>
              {paths.map((p) => (
                <th
                  key={p.player_type}
                  style={{
                    textAlign: "right",
                    padding: "6px 8px",
                    fontSize: "11px",
                    fontWeight: 500,
                  }}
                >
                  {PLAYER_LABEL[p.player_type] ?? p.player_type}
                </th>
              ))}
              <th style={{ textAlign: "right", padding: "6px 8px" }}>合计</th>
            </tr>
          </thead>
          <tbody>
            {prodKeys.map((key, i) => (
              <tr key={key} style={{ borderBottom: "1px solid var(--line-soft, var(--line))" }}>
                <td style={{ padding: "5px 8px", color: "var(--ink-faint)" }}>{key} 级</td>
                {paths.map((p) => (
                  <td
                    key={p.player_type}
                    style={{
                      padding: "5px 8px",
                      textAlign: "right",
                      fontVariantNumeric: "tabular-nums",
                    }}
                  >
                    {(p.production_class_summary ?? {})[key] ?? 0}
                  </td>
                ))}
                <td
                  style={{
                    padding: "5px 8px",
                    textAlign: "right",
                    fontWeight: 500,
                    fontVariantNumeric: "tabular-nums",
                  }}
                >
                  {prodSums[i]}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : null}
    </Panel>
  );
}

/* ------------------------------------------------------------- 单路径回放（§12.4） */

/** 真机步骤形状（`simulator.Step.as_dict()`）；老快照可能缺 `delta` / `state`。 */
interface ReplayStep {
  storylet_id?: string;
  title?: string;
  choice_id?: string | null;
  option_id?: string | null;
  delta?: Record<string, unknown>;
}

/** 状态增量的展示：数值带符号，非数值原样。 */
function formatDelta(value: unknown): string {
  const num = Number(value);
  if (Number.isFinite(num)) return num > 0 ? `+${num}` : String(num);
  return String(value);
}

/**
 * 单路径回放 + 状态时间线（需求 §12.4）。
 *
 * 数据来自报告端点已返回的 `paths[].steps`（与 `state_timeline` 同源），**不新增路由**
 * （`specs/03` §11.2 维持 ➖）。只呈现、不判定：路径合法性由后端 `failures` 决定。
 * 老快照缺 `delta` / `state` 时对应区块自动隐藏，不臆造数值。
 */
function PathReplay({ path }: { path: SimulationPath }) {
  const steps = (path.steps ?? []) as unknown as ReplayStep[];
  const changedKeys = [...new Set(steps.flatMap((step) => Object.keys(step.delta ?? {})))].sort();

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div className="meta-row">
        <Tag>{PLAYER_LABEL[path.player_type] ?? path.player_type}</Tag>
        <Tag tone={path.failures.length > 0 ? "red" : "green"}>{path.ending_id ?? "无结局"}</Tag>
        <Tag>{steps.length} 步</Tag>
      </div>
      {path.ending_explanation ? <p className="inline-note">{path.ending_explanation}</p> : null}

      <section>
        <h3>逐步回放</h3>
        {steps.length === 0 ? (
          <p className="inline-note">这条路径没有步骤记录。</p>
        ) : (
          <div>
            {steps.map((step, index) => (
              <div className="replay-step" key={`${step.storylet_id ?? "step"}-${index}`}>
                <div className="meta-row">
                  <Tag>{String(index + 1).padStart(2, "0")}</Tag>
                  <strong style={{ fontSize: "12px" }}>
                    {step.title || step.storylet_id || "—"}
                  </strong>
                  <small style={{ color: "var(--ink-faint)" }}>{step.storylet_id}</small>
                </div>
                {step.choice_id ? (
                  <p className="inline-note">
                    {step.choice_id} → {step.option_id ?? "—"}
                  </p>
                ) : null}
                {step.delta && Object.keys(step.delta).length > 0 ? (
                  <div className="meta-row" style={{ marginTop: 4 }}>
                    {Object.entries(step.delta).map(([key, value]) => (
                      <Tag key={key} tone="blue">
                        {key} {formatDelta(value)}
                      </Tag>
                    ))}
                  </div>
                ) : null}
              </div>
            ))}
          </div>
        )}
      </section>

      {changedKeys.length > 0 ? (
        <section>
          <h3>状态时间线</h3>
          <table className="replay-timeline">
            <thead>
              <tr>
                <th>状态</th>
                <th>变化序列（步号: 值）</th>
              </tr>
            </thead>
            <tbody>
              {changedKeys.map((key) => (
                <tr key={key}>
                  <td>{key}</td>
                  <td>
                    {steps.map((step, index) =>
                      step.delta && key in step.delta ? (
                        <span key={index} style={{ marginLeft: 8 }}>
                          #{index + 1} {formatDelta(step.delta[key])}
                        </span>
                      ) : null,
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      ) : null}
    </div>
  );
}
